import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Overlay, OverlayConfig } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Injectable, Injector, inject } from '@angular/core';
import {
  BbitLegalFilesResponse,
  BbitString,
  IBbitLegalFile,
  IBbitPlaceBookingMetaResponse,
} from '@bbitgmbh/bbit.utils';
import { BbitWebsiteService } from '@bbitgmbh/website.sdk';
import { Duration } from 'luxon';
import { BookingInfoOverlayComponent } from './booking-info-overlay/booking-info-overlay.component';
import { BookingInfoOverlayRef } from './booking-info-overlay/booking-info-overlay.ref';

export interface IBbitBookingOption {
  allowDurationChange?: boolean;
  key: string;
  title: string;
  designation: string;
  places?: string[];
  image?: string;
  usageTypes?: { _id: string; text: string }[];
  snapGrid?: Duration;
  startOffset?: Duration;
  duration?: Duration;
}

export interface IAccess {
  credentials: { barcodeNumber: string; pin: string };
  nextBookings: { from: string; until: string }[];
}

@Injectable({
  providedIn: 'root',
})
export class PlaceBookingService {
  private _overlay = inject(Overlay);
  private _breakpointObserver = inject(BreakpointObserver);
  private _website = inject(BbitWebsiteService);
  public bookingOptions: IBbitBookingOption[] = [
    {
      key: 'indoor',
      title: 'Halle',
      designation: `
<p>Für den normalen Trainingsbetrieb buchst du eine Hallenhälfte.</p>
<p>Für Kurse/Seminare/Parcourstrainings kann auf <a class="text-primary-600" href="mailto:daniela.weber@hundesport-sg.ch" target="_blank">Anfrage</a> die ganze Halle gemietet werden.</p>
<p>Eine Trainingseinheit inkl. Auf- und Abbau dauert 75 Minuten.</p>
<div class="flex flex-col mb-4">
  <div class="text-sm text-gray-600 mb-1">Trainingsfläche</div>
  <div>24m x 47.85m</div>
</div>
<div class="flex flex-col mb-4">
  <div class="text-sm text-gray-600 mb-1">Beginn Einheiten</div>
  <div>06.45 | 08.00 | 09.15 | 10.30 | 11.45 | 13.00 | 14.15 | 15.30 | 16.45 | 18.00 | 19.15 | 20.30</div>
</div>
<div class="flex flex-col mb-4">
  <div class="text-sm text-gray-600 mb-1">Zugang</div>
  <div>15 Minuten vor bis 30 Minuten nach der Einheit mit persönlichem QR-Code oder PIN + #</div>
</div>

<p>Halbjahres- und Jahreseinheiten auf <a class="text-primary-600" href="mailto:daniela.weber@hundesport-sg.ch" target="_blank">Anfrage</a>.</p>`,
      places: ['#arYxa6sT3eUMs9GJ7eZW2', '#cTZGXaY4ELntbqMKpkFPW'],
      image: '/assets/images/halle-1.png',
      allowDurationChange: true,
      startOffset: Duration.fromObject({ minutes: 45 }),
      snapGrid: Duration.fromObject({ minutes: 75 }),
      duration: Duration.fromObject({ minutes: 75 }),
    },
    {
      key: 'outdoor',
      title: 'Aussenplatz',
      designation: `
<p>Der ganze Aussenplatz kann im Viertelstundenraster gebucht werden. Die kleinste Einheit beträgt 15 Minuten.</p>
<div class="flex flex-col mb-4">
  <div class="text-sm text-gray-600 mb-1">Trainingsfläche</div>
  <div>20m x 40m</div>
</div>
<div class="flex flex-col mb-4">
  <div class="text-sm text-gray-600 mb-1">Agilityparcours</div>
  <div>Einsatz Pneu, Mauer und Weitsprung mit Nutzer*innen Halle der absprechen.</div>
</div>
<div class="flex flex-col mb-4">
  <div class="text-sm text-gray-600 mb-1">Zugang WC-Anlage</div>
  <div>15 Minuten vor bis 30 Minuten nach der Einheit mit persönlichem QR-Code oder PIN + #</div>
</div>`,
      places: ['#UCYx2j5x58HbG3ky2Xrzw'],
      image: '/assets/images/aussenplatz.png',
      allowDurationChange: true,
    },
  ];

  public getBookingOption(key: string): IBbitBookingOption | undefined {
    return this.bookingOptions.find((option) => option.key === key);
  }

  public async getAccess(): Promise<IAccess> {
    const response = await fetch(`${this._website.getBlApiUrl()}/web/access`, {
      method: 'GET',
      headers: {
        Authorization: `${this._website.getAccessToken()}`,
      },
    });
    const data = await response.json();
    return data;
  }

  public async getMeta(params?: { place?: string; usageType?: string }): Promise<IBbitPlaceBookingMetaResponse> {
    const metaResponse = await fetch(`${this._website.getBlApiUrl()}/placeBooking/meta`, {
      method: 'POST',
      headers: {
        Authorization: `${this._website.getAccessToken()}`,
      },
      body: params ? JSON.stringify(params) : undefined,
    });
    const meta = await metaResponse.json();
    return meta;
  }

  public async getLegalFiles(): Promise<BbitLegalFilesResponse> {
    const response = await fetch(`${this._website.getBlApiUrl()}/placeBooking/legalFiles`, {
      method: 'GET',
    });
    if (!response.ok) {
      throw new Error('Failed to fetch legal files');
    }
    const files = await response.json();
    return files;
  }

  public getDownloadUrl(file: IBbitLegalFile): string {
    return `${this._website.getBlApiUrl()}/placeBooking/legalFile/${BbitString.stripBeginningHashtag(file._id)}`;
  }

  public async openInfoOverlay(): Promise<void> {
    const overlayConfig = new OverlayConfig({
      hasBackdrop: true,
      backdropClass: 'overlay-backdrop',
      panelClass: '',
      scrollStrategy: this._overlay.scrollStrategies.block(),
      positionStrategy: this._overlay.position().global().centerHorizontally().centerVertically(),
    });
    const overlayRef = this._overlay.create(overlayConfig);
    const overlayRefInstance = new BookingInfoOverlayRef(overlayRef);
    const injector = Injector.create({
      providers: [
        { provide: BookingInfoOverlayRef, useValue: overlayRefInstance },
        { provide: BbitWebsiteService, useValue: this._website },
      ],
    });
    const containerPortal = new ComponentPortal(BookingInfoOverlayComponent, null, injector);
    overlayRef.attach(containerPortal);
    const breakpoint = this._breakpointObserver.observe(Breakpoints.XSmall).subscribe((size) => {
      if (size.matches) {
        overlayRef.updateSize({ width: '100%', height: '100%' });
      } else {
        overlayRef.updateSize({
          minWidth: '400px',
          maxWidth: '600px',
          width: '80%',
          height: '80%',
          maxHeight: '80%',
        });
      }
    });
    overlayRef.backdropClick().subscribe((): void => {
      breakpoint.unsubscribe();
      overlayRefInstance.close();
    });
  }
}
